import { type GroupChannel } from '@sendbird/chat/groupChannel';
import { useEffect, useState } from 'react';

import type { MembershipDetails } from '../../entities/entityTypes';
import { captureException } from '../../logger/Logger';
import { useSendbird } from './useSendbird';

interface UseGetSendbirdChannelResult {
    channel: GroupChannel | null;
    isLoading: boolean;
}

const getMembershipMessagingChannelUrl = (
    membership: MembershipDetails,
): string | null | undefined => membership.messagingChannel?.channelUrl;

// Fetches a Sendbird channel for a given membership.
export const useGetSendbirdChannel = (
    membership: MembershipDetails,
): UseGetSendbirdChannelResult => {
    const { sdk: sendbirdSdk, isLoading: isLoadingSendbird } = useSendbird();
    const channelUrl = getMembershipMessagingChannelUrl(membership);
    const [isLoading, setIsLoading] = useState(() => channelUrl != null);
    const [channel, setChannel] = useState<GroupChannel | null>(null);

    useEffect(() => {
        if (channelUrl == null) {
            setChannel(null);
            setIsLoading(false);
            return;
        }

        if (isLoadingSendbird) {
            setChannel(null);
            setIsLoading(true);
            return;
        }

        if (sendbirdSdk == null) {
            setChannel(null);
            setIsLoading(false);
            return;
        }

        let isCancelled = false;

        setChannel(null);
        setIsLoading(true);

        const getChannel = async () => {
            try {
                const fetchedChannel =
                    await sendbirdSdk.groupChannel.getChannel(channelUrl);

                if (fetchedChannel == null) {
                    captureException(
                        new Error(
                            'Failed to fetch messaging channel for the given membership.',
                        ),
                    );
                }

                if (!isCancelled) {
                    setChannel(fetchedChannel ?? null);
                    setIsLoading(false);
                }
            } catch (error) {
                captureException(error);
                if (!isCancelled) {
                    setChannel(null);
                    setIsLoading(false);
                }
            }
        };

        void getChannel();

        return () => {
            isCancelled = true;
        };
    }, [channelUrl, isLoadingSendbird, sendbirdSdk]);

    return { channel, isLoading };
};
